import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../utils/apiConfig';
import { getAuthHeaders } from '../../utils/authUtils';

/**
 * PriorReportTextView - Komponente zur Anzeige von Vorbefunden als Plain Text
 * 
 * Wird in ApprovalWorkflow.js und ReportEditor.js verwendet.
 * Zeigt den Befundtext ohne Formatierung an und bietet Buttons zum
 * Zurückkehren zur Liste oder zum Öffnen der PDF-Ansicht.
 * 
 * Props:
 * - study: Das Prior Study Objekt mit study_instance_uid
 * - onBack: Callback wenn "Zurück" geklickt wird
 * - onShowPdf: Callback wenn "PDF anzeigen" geklickt wird (mit reportData als Parameter)
 */
const PriorReportTextView = ({ study, onBack, onShowPdf }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportText, setReportText] = useState('');
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    if (study && study.study_instance_uid) {
      loadReportText();
    }
  }, [study]);

  const loadReportText = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 1. Zuerst Report-ID über study_instance_uid laden
      const reportResponse = await apiFetch(`/reports/by-study-uid/${study.study_instance_uid}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!reportResponse.ok) {
        if (reportResponse.status === 404) {
          setError(t('priorReport.error.noReportFound', 'Für diese Voruntersuchung wurde kein Befund gefunden.'));
        } else {
          throw new Error('Fehler beim Laden des Befunds');
        }
        setLoading(false);
        return;
      }

      const reportDataResult = await reportResponse.json();
      
      // Speichere Report-Daten für PDF-Ansicht
      setReportData(reportDataResult);

      // 2. Plain Text vom Backend laden
      const textResponse = await apiFetch(`/reports/${reportDataResult.report_id}/plain-text`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!textResponse.ok) {
        throw new Error('Fehler beim Laden des Befundtextes');
      }

      const textData = await textResponse.json();

      if (textData.plain_text && textData.plain_text.trim()) {
        setReportText(textData.plain_text);
      } else if (textData.success !== false) {
        setError(t('priorReport.error.noContent', 'Kein Befundtext verfügbar.'));
      } else {
        setError(t('priorReport.error.noTextFound', 'Befundtext konnte nicht geladen werden.'));
      }

    } catch (err) {
      console.error('Fehler beim Laden des Befundtextes:', err);
      setError(t('priorReport.error.loadFailed', 'Fehler beim Laden des Befundtextes: ') + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShowPdf = () => {
    if (reportData && reportData.pdf_data && onShowPdf) {
      onShowPdf(reportData);
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1) !important',
      }}
    >
      {/* Header mit Titel und Buttons */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid var(--rd-divider)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onBack} size="small" sx={{ color: 'var(--rd-primary)' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ color: 'var(--rd-primary)' }}>
            {t('priorReport.title', 'Vorbefund')}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            onClick={handleShowPdf}
            disabled={!reportData || !reportData.pdf_data}
            sx={{ 
              color: 'var(--rd-primary)',
              '&:hover': {
                backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'
              },
              '&.Mui-disabled': {
                color: 'color-mix(in srgb, var(--rd-primary) 30%, transparent)'
              }
            }}
            title={t('priorReport.showPdf', 'PDF anzeigen')}
          >
            <PdfIcon fontSize="medium" />
          </IconButton>
        </Box>
      </Box>

      {/* Study Info */}
      {study && (
        <Box sx={{ 
          p: 2, 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
          borderBottom: '1px solid var(--rd-divider)'
        }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('priorReport.studyDate', 'Untersuchungsdatum')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {study.study_date ? new Date(study.study_date).toLocaleDateString('de-DE') : '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('priorReport.modality', 'Modalität')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {study.modality || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('priorReport.author', 'Befunder')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {study.initial_author_name || '-'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('priorReport.description', 'Beschreibung')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {study.study_description || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Content Area */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 3
      }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress sx={{ color: 'var(--rd-primary)' }} />
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" variant="body1">
              {error}
            </Typography>
            <Button 
              onClick={loadReportText} 
              sx={{ mt: 2 }}
              className="button"
            >
              {t('priorReport.retry', 'Erneut versuchen')}
            </Button>
          </Box>
        )}

        {!loading && !error && reportText && (
          <Box 
            sx={{ 
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'var(--rd-bg-default)',
              p: 3,
              borderRadius: 1,
              border: '1px solid var(--rd-divider)',
              // Text ist selektierbar und kopierbar
              userSelect: 'text',
              cursor: 'text'
            }}
          >
            {reportText}
          </Box>
        )}

        {!loading && !error && !reportText && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary" variant="body1">
              {t('priorReport.error.noContent', 'Kein Befundtext verfügbar.')}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PriorReportTextView;

